import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { stashPendingLegalAcceptance } from '@/lib/legalAcceptance';
import { LEGAL_VERSION } from '@/lib/marketing/legal/version';
import AuthMarketingPanel, { HiroMark } from '@/components/auth/AuthMarketingPanel';
import BrandLoadingScreen from '@/components/shared/BrandLoadingScreen';
import { cn } from '@/lib/utils';
import { PageSeo } from '@/components/seo/PageSeo';
import { NOINDEX_FOLLOW } from '@/lib/seo/constants';
import { signupSeo } from '@/lib/seo/authSeo';

const SignupPage = () => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [legalError, setLegalError] = useState('');
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [acceptedOnerousClauses, setAcceptedOnerousClauses] = useState(false);
  const legalSectionRef = useRef<HTMLDivElement>(null);
  const { signup, loginWithGoogle, isLoading, user } = useAuth();
  const { t, language } = useLanguage();
  const isIt = language === 'it';
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirectTo = searchParams.get('redirect') ?? '/dashboard';

  const onboardingUrl =
    redirectTo !== '/dashboard'
      ? `/onboarding?fromSignup=true&skipCompany=true&redirect=${encodeURIComponent(redirectTo)}`
      : '/onboarding?fromSignup=true';

  // Fully onboarded users skip signup.
  useEffect(() => {
    if (user && Object.keys(user.memberships ?? {}).length > 0) {
      navigate(redirectTo, { replace: true });
    }
  }, [user, redirectTo, navigate]);

  useEffect(() => {
    if (acceptedTerms && acceptedOnerousClauses) {
      setLegalError('');
    }
  }, [acceptedTerms, acceptedOnerousClauses]);

  const legalGateOk = acceptedTerms && acceptedOnerousClauses;

  const goToCompanyOnboarding = () => {
    navigate(onboardingUrl, { replace: true });
  };

  const ensureLegalGate = (): boolean => {
    if (legalGateOk) {
      setLegalError('');
      return true;
    }
    const msg = isIt
      ? 'Devi accettare i Termini di Servizio e approvare le clausole indicate per continuare.'
      : 'You must accept the Terms of Service and approve the listed clauses to continue.';
    setLegalError(msg);
    requestAnimationFrame(() => {
      legalSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
    return false;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!ensureLegalGate()) return;
    if (password !== confirm) {
      setError(t('signup.passwordMismatch'));
      return;
    }
    setError('');
    try {
      stashPendingLegalAcceptance();
      await signup(name, email, password);
      goToCompanyOnboarding();
    } catch (err: any) {
      setError(err?.message || t('signup.error'));
    }
  };

  const handleGoogleSignup = async () => {
    if (!ensureLegalGate()) return;
    setError('');
    try {
      stashPendingLegalAcceptance();
      await loginWithGoogle();
      goToCompanyOnboarding();
    } catch (err: any) {
      if (err?.code === 'NEW_GOOGLE_USER') {
        goToCompanyOnboarding();
        return;
      }
      setError(t('signup.googleFailed'));
    }
  };

  const seo = signupSeo(language);

  if (isLoading) {
    return (
      <>
        <PageSeo title={seo.title} path="/signup" robots={NOINDEX_FOLLOW} />
        <BrandLoadingScreen />
      </>
    );
  }

  return (
    <div className="min-h-screen lg:h-screen lg:overflow-hidden grid grid-cols-1 lg:grid-cols-2 bg-brand-surface-light">
      <PageSeo title={seo.title} path="/signup" robots={NOINDEX_FOLLOW} />
      <AuthMarketingPanel />

      <div className="lg:h-screen lg:overflow-y-auto">
        <div className="flex min-h-full flex-col items-center justify-center px-5 sm:px-8 py-10 lg:py-12">
          <div className="w-full max-w-sm">
            <div className="flex lg:hidden items-center justify-center gap-2 mb-8">
              <HiroMark className="h-9 w-9" />
              <span className="font-display font-bold text-xl tracking-tight">HiroMatch</span>
            </div>

            <div className="mb-6">
              <h1 className="font-display font-bold text-3xl tracking-tight text-foreground leading-tight">
                {isIt ? 'Inizia il tuo trial gratuito di 7 giorni' : 'Start your free 7-day trial'}
              </h1>
              <p className="text-sm text-muted-foreground mt-2">
                {isIt
                  ? 'Nessuna carta richiesta. Cancellabile in qualsiasi momento.'
                  : 'No credit card required. Cancel anytime.'}
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              {error && <p className="text-sm text-destructive">{error}</p>}
              <div className="space-y-2">
                <Label htmlFor="name">{t('signup.name')}</Label>
                <Input id="name" value={name} onChange={(e) => setName(e.target.value)} required placeholder={t('signup.namePlaceholder')} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">{t('signup.email')}</Label>
                <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required placeholder="you@company.com" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">{t('signup.password')}</Label>
                <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required placeholder="••••••••" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirm">{t('signup.confirm')}</Label>
                <Input id="confirm" type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} required placeholder="••••••••" />
              </div>

              <div
                ref={legalSectionRef}
                className={cn(
                  'space-y-3 rounded-md border bg-card/50 p-4 text-sm',
                  legalError ? 'border-destructive/60' : 'border-border/60',
                )}
              >
                {legalError && <p className="text-sm text-destructive">{legalError}</p>}
                <div className="flex items-start gap-3">
                  <Checkbox
                    id="accept-terms"
                    checked={acceptedTerms}
                    onCheckedChange={(v) => setAcceptedTerms(v === true)}
                    className="mt-0.5"
                  />
                  <label htmlFor="accept-terms" className="leading-relaxed text-muted-foreground cursor-pointer">
                    {isIt ? (
                      <>
                        Dichiaro di agire nell&apos;ambito della mia attività professionale o imprenditoriale e accetto i{' '}
                        <Link to="/terms" target="_blank" className="text-brand-accent underline-offset-4 hover:underline">
                          Termini di Servizio
                        </Link>{' '}
                        (versione {LEGAL_VERSION}) e l&apos;{' '}
                        <Link to="/privacy" target="_blank" className="text-brand-accent underline-offset-4 hover:underline">
                          Informativa Privacy
                        </Link>
                        .
                      </>
                    ) : (
                      <>
                        I declare that I act in the course of my professional or business activity and accept the{' '}
                        <Link to="/terms" target="_blank" className="text-brand-accent underline-offset-4 hover:underline">
                          Terms of Service
                        </Link>{' '}
                        (version {LEGAL_VERSION}) and the{' '}
                        <Link to="/privacy" target="_blank" className="text-brand-accent underline-offset-4 hover:underline">
                          Privacy Policy
                        </Link>
                        .
                      </>
                    )}
                  </label>
                </div>
                <div className="flex items-start gap-3">
                  <Checkbox
                    id="accept-onerous"
                    checked={acceptedOnerousClauses}
                    onCheckedChange={(v) => setAcceptedOnerousClauses(v === true)}
                    className="mt-0.5"
                  />
                  <label htmlFor="accept-onerous" className="leading-relaxed text-muted-foreground cursor-pointer">
                    {isIt
                      ? 'Ai sensi degli artt. 1341 e 1342 c.c., approvo specificamente le seguenti clausole dei Termini di Servizio: art. 2 (modifica del Servizio), art. 3 (sospensione e chiusura account), art. 4 (rinnovo automatico, sospensione per mancato pagamento, esclusione rimborsi), art. 9 (limitazione di responsabilità), art. 10 (manleva), art. 11 (risoluzione e sospensione), art. 12 (forza maggiore e recesso), art. 13 (foro esclusivo di Roma e legge applicabile).'
                      : 'Pursuant to Articles 1341 and 1342 of the Italian Civil Code, I specifically approve the following clauses of the Terms of Service: Art. 2 (changes to the Service), Art. 3 (account suspension and closure), Art. 4 (automatic renewal, suspension for non-payment, refund exclusions), Art. 9 (limitation of liability), Art. 10 (indemnification), Art. 11 (termination and suspension), Art. 12 (force majeure and withdrawal), Art. 13 (exclusive jurisdiction of Rome and governing law).'}
                  </label>
                </div>
              </div>

              <Button
                type="submit"
                className="w-full bg-brand-primary hover:bg-brand-primary-mid text-white"
                disabled={isLoading || !legalGateOk}
              >
                {isLoading ? t('signup.submitting') : t('signup.submit')}
              </Button>

              <div className="relative py-1">
                <div className="absolute inset-0 flex items-center"><span className="w-full border-t border-border" /></div>
                <div className="relative flex justify-center"><span className="bg-brand-surface-light px-2 text-xs text-muted-foreground uppercase tracking-wide">{isIt ? 'oppure' : 'or'}</span></div>
              </div>

              <Button
                type="button"
                variant="outline"
                className="w-full gap-2"
                disabled={isLoading}
                onClick={handleGoogleSignup}
              >
                <svg className="h-4 w-4" viewBox="0 0 24 24"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/></svg>
                {t('signup.google')}
              </Button>
            </form>

            <p className="text-center text-sm text-muted-foreground mt-8">
              {isIt ? 'Hai già un account? ' : 'Already have an account? '}
              <Link
                to={redirectTo !== '/dashboard' ? `/login?redirect=${encodeURIComponent(redirectTo)}` : '/login'}
                className="text-brand-accent hover:text-brand-primary font-medium"
              >
                {isIt ? 'Accedi →' : 'Sign in →'}
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SignupPage;
